import { useState } from 'react';
import { useGT } from 'gt-react';
import { CaretDownIcon, CheckIcon, XIcon } from '@phosphor-icons/react';
import { useDebouncedState } from '@react-hookz/web';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { useArInvoicesList } from '@/api/rpc/hooks/accounts-receivable/useArInvoices';
import { useArInvoice } from '@/api/rpc/hooks/accounts-receivable/useArInvoices';
import { Currency } from '@/components/common/Currency';

interface InvoicePickerComboboxProps {
  /** Restricts results to invoices for this customer. */
  customerId?: string;
  value: string | null;
  onChange: (invoiceId: string | null) => void;
  /** Visible only in the trigger when no invoice is selected. */
  placeholder?: string;
  disabled?: boolean;
  /** Render trigger in compact form (smaller height, no left padding). */
  compact?: boolean;
}

/**
 * Searchable invoice picker. Server-side `search` filter hits invoice number,
 * customer name, customer code, and PO ref — same fields as the AR invoices
 * page search box. Use `value: null` to mean "no invoice".
 */
export function InvoicePickerCombobox({
  customerId,
  value,
  onChange,
  placeholder,
  disabled,
  compact = false,
}: InvoicePickerComboboxProps) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useDebouncedState('', 250);

  const filters = {
    ...(customerId ? { customerId: [customerId] } : {}),
    ...(search.length >= 1 ? { search } : {}),
    limit: 25,
  };
  const { invoices, isLoading } = useArInvoicesList(filters);

  // Resolve the currently selected invoice so the trigger can show its
  // number even when it isn't in the current search results.
  const { invoice: currentInvoice } = useArInvoice(value ?? undefined);

  const triggerLabel = (() => {
    if (!value) {
      return (
        placeholder ??
        gt('No invoice linked', {
          context: 'placeholder, invoice picker no selection',
        })
      );
    }
    if (currentInvoice) {
      return currentInvoice.invoiceNumber ?? currentInvoice.id.slice(0, 8);
    }
    // Loading current selection
    return value.slice(0, 8);
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={`justify-between ${compact ? 'h-8 px-2 text-xs' : 'w-full'}`}
        >
          <span className="truncate">{triggerLabel}</span>
          <CaretDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={gt('Search by invoice #, customer, or PO ref...', {
              context: 'placeholder, invoice picker search',
            })}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading
                ? gt('Searching...', {
                    context: 'loading state, invoice picker',
                  })
                : gt('No invoices found', {
                    context: 'empty state, invoice picker',
                  })}
            </CommandEmpty>

            {value && (
              <CommandGroup>
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                  className="text-muted-foreground"
                >
                  <XIcon className="mr-2 h-4 w-4" />
                  {gt('Clear selection', {
                    context: 'menu item, clear invoice picker',
                  })}
                </CommandItem>
              </CommandGroup>
            )}

            <CommandGroup>
              {invoices.map((inv) => {
                const isSelected = inv.id === value;
                return (
                  <CommandItem
                    key={inv.id}
                    value={inv.id}
                    onSelect={() => {
                      onChange(inv.id);
                      setOpen(false);
                    }}
                  >
                    <CheckIcon
                      className={`mr-2 h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`}
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="font-medium text-sm truncate">
                          {inv.invoiceNumber ?? inv.id.slice(0, 8)}
                        </span>
                        <Currency
                          value={inv.balanceDue}
                          className="text-xs text-muted-foreground"
                          fallback="—"
                        />
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {[
                          inv.customerName,
                          inv.customerPurchaseOrderReference
                            ? `PO ${inv.customerPurchaseOrderReference}`
                            : null,
                          inv.status,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
